import { motion } from 'framer-motion'
import { useState } from 'react'
import { useLanguage } from '../contexts/LanguageContext'
import ScrollArrow from '../components/ScrollArrow'
import GlareCard from '../components/ui/GlareCard'
import ProgressiveImage from '../components/ui/ProgressiveImage'
import { textReveal, staggerContainer, staggerItem } from '../animations/variants'

const Contact = () => {
  const { t } = useLanguage()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  })

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const mailtoLink = `mailto:contact@xdt.com?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(
      `${t('contact.email.name')}: ${formData.name}\n${t('contact.email.company')}: ${formData.company}\n${t('contact.email.email')}: ${formData.email}\n\n${t('contact.email.message')}:\n${formData.message}`
    )}`
    window.location.href = mailtoLink
  }

  const contactMethods = [
    {
      icon: 'mail',
      title: t('contact.methods.email.title'),
      details: "contact@xdt.com",
      description: t('contact.methods.email.desc')
    },
    {
      icon: 'phone',
      title: t('contact.methods.phone.title'),
      details: "+86 400-123-4567",
      description: t('contact.methods.phone.desc')
    },
    {
      icon: 'location_on',
      title: t('contact.methods.address.title'),
      details: t('contact.methods.address.details'),
      description: t('contact.methods.address.desc')
    },
    {
      icon: 'chat',
      title: t('contact.methods.chat.title'),
      details: t('contact.methods.chat.details'),
      description: t('contact.methods.chat.desc')
    }
  ]

  const businessTypes = [
    t('contact.form.types.product'),
    t('contact.form.types.technical'),
    t('contact.form.types.business'),
    t('contact.form.types.media'),
    t('contact.form.types.recruitment'),
    t('contact.form.types.other')
  ]

  return (
    <div className="bg-black">
      {/* Hero Section - Full Screen */}
      <section className="section-fullscreen">
        <div className="section-bg">
          <ProgressiveImage
            src="/images/hero-contact.jpg"
            placeholder="/images/placeholders/hero-contact-placeholder.jpg"
            alt="Modern Building"
          />
          <div className="section-overlay" />
          <div className="section-overlay-left" />
        </div>

        <div className="section-content">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.div variants={textReveal} className="section-label">CONTACT</motion.div>
            <motion.h1 variants={textReveal} className="section-heading text-glow">
              {t('contact.hero.title')}
            </motion.h1>
            <motion.p variants={textReveal} className="section-description">
              {t('contact.hero.subtitle')}
            </motion.p>
          </motion.div>
        </div>

        <ScrollArrow />
      </section>

      {/* Contact Methods - 网格纹理背景 */}
      <section className="relative z-10 py-24 px-6 md:px-20 bg-grid-texture">
        {/* 顶部渐变过渡 */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black to-transparent pointer-events-none" />
        {/* 底部渐变过渡 */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent pointer-events-none" />

        <div className="relative max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div variants={textReveal} className="section-label justify-center flex mb-6">GET IN TOUCH</motion.div>
            <motion.h2 variants={textReveal} className="text-3xl md:text-4xl font-bold text-white mb-6 uppercase tracking-[-0.02em]">
              {t('contact.methods.title')}
            </motion.h2>
            <motion.p variants={textReveal} className="text-gray-400 font-mono text-sm max-w-3xl mx-auto">
              {t('contact.methods.subtitle')}
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactMethods.map((method, index) => (
              <GlareCard key={index} className="text-center">
                <span className="material-symbols-outlined text-3xl text-white/30 mb-4">{method.icon}</span>
                <h3 className="section-title mb-3">{method.title}</h3>
                <p className="font-mono text-white/70 mb-2 text-sm">
                  {method.details}
                </p>
                <p className="text-gray-500 text-sm font-mono">
                  {method.description}
                </p>
              </GlareCard>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form - 渐变纹理背景 */}
      <section className="relative z-10 py-24 px-6 md:px-20 bg-gradient-texture">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <motion.div variants={textReveal} className="section-label justify-center flex mb-6">SEND MESSAGE</motion.div>
            <motion.h2 variants={textReveal} className="text-3xl md:text-4xl font-bold text-white mb-6 uppercase tracking-[-0.02em]">
              {t('contact.form.title')}
            </motion.h2>
            <motion.p variants={textReveal} className="text-gray-400 font-mono text-sm">
              {t('contact.form.subtitle')}
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <form onSubmit={handleSubmit} className="metal-surface p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-[10px] font-mono text-gray-500 mb-2 uppercase tracking-widest">
                    {t('contact.form.name.label')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-white/30 focus:outline-none transition-colors font-mono text-sm"
                    placeholder={t('contact.form.name.placeholder')}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-[10px] font-mono text-gray-500 mb-2 uppercase tracking-widest">
                    {t('contact.form.email.label')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-white/30 focus:outline-none transition-colors font-mono text-sm"
                    placeholder={t('contact.form.email.placeholder')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="company" className="block text-[10px] font-mono text-gray-500 mb-2 uppercase tracking-widest">
                    {t('contact.form.company.label')}
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-white/30 focus:outline-none transition-colors font-mono text-sm"
                    placeholder={t('contact.form.company.placeholder')}
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-[10px] font-mono text-gray-500 mb-2 uppercase tracking-widest">
                    {t('contact.form.subject.label')}
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-white/30 focus:outline-none transition-colors font-mono text-sm"
                  >
                    <option value="">{t('contact.form.subject.placeholder')}</option>
                    {businessTypes.map((type, index) => (
                      <option key={index} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-[10px] font-mono text-gray-500 mb-2 uppercase tracking-widest">
                  {t('contact.form.message.label')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={6}
                  required
                  value={formData.message}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-white/30 focus:outline-none transition-colors resize-none font-mono text-sm"
                  placeholder={t('contact.form.message.placeholder')}
                />
              </div>

              <button
                type="submit"
                className="section-cta w-full md:w-auto justify-center"
              >
                <span>{t('contact.form.submit')}</span>
                <span className="material-symbols-outlined text-lg">send</span>
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section - 深灰背景 */}
      <section className="relative z-10 py-24 px-6 md:px-20 bg-dark-gray">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <motion.div variants={textReveal} className="section-label justify-center flex mb-6">FAQ</motion.div>
            <motion.h2 variants={textReveal} className="text-3xl md:text-4xl font-bold text-white mb-6 uppercase tracking-[-0.02em]">
              {t('contact.faq.title')}
            </motion.h2>
            <motion.p variants={textReveal} className="text-gray-400 font-mono text-sm">
              {t('contact.faq.subtitle')}
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            <motion.div variants={staggerItem} className="metal-surface p-6 hover:border-white/20 transition-colors">
              <h3 className="font-bold text-white mb-3 uppercase tracking-widest text-sm">{t('contact.faq.trial.question')}</h3>
              <p className="text-gray-500 text-sm font-mono leading-relaxed">
                {t('contact.faq.trial.answer')}
              </p>
            </motion.div>

            <motion.div variants={staggerItem} className="metal-surface p-6 hover:border-white/20 transition-colors">
              <h3 className="font-bold text-white mb-3 uppercase tracking-widest text-sm">{t('contact.faq.tech.question')}</h3>
              <p className="text-gray-500 text-sm font-mono leading-relaxed">
                {t('contact.faq.tech.answer')}
              </p>
            </motion.div>

            <motion.div variants={staggerItem} className="metal-surface p-6 hover:border-white/20 transition-colors">
              <h3 className="font-bold text-white mb-3 uppercase tracking-widest text-sm">{t('contact.faq.support.question')}</h3>
              <p className="text-gray-500 text-sm font-mono leading-relaxed">
                {t('contact.faq.support.answer')}
              </p>
            </motion.div>

            <motion.div variants={staggerItem} className="metal-surface p-6 hover:border-white/20 transition-colors">
              <h3 className="font-bold text-white mb-3 uppercase tracking-widest text-sm">{t('contact.faq.cooperation.question')}</h3>
              <p className="text-gray-500 text-sm font-mono leading-relaxed">
                {t('contact.faq.cooperation.answer')}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Contact
